import { soltarDoAparelho } from "../services/notificacoesDoChat";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useToast } from "./ToastContext";
import { mensagemDeErro } from "../utils/errosApi";
import { clearAuthenticatedImageCache } from "../hooks/useAuthenticatedImageUrl";
import { limparSnapshots } from "../utils/painelRegistry";
import { limparChavesDoTenant } from "../utils/tenantStorage";
import { ACCESS_API_URL } from "../config/api";
import { verificadorDeModulos } from "../config/modulos";
import { ehHostDoPainel } from "../utils/hostDoPainel";

// Base usada para resolver URLs relativas de mídia vindas do backend. É a
// mesma origem da API (fonte única em `src/config/api.js`) — o aviso sobre a
// variável de ambiente ausente também vive lá.
const DEFAULT_ACCESS_API_URL = ACCESS_API_URL;

const STORAGE_KEY = "pharuerp:access-session";

const AuthContext = createContext(null);

function readStoredSession() {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? normalizeSession(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

/** O pedido era de quem ja saiu: quem chama descarta o resultado, sem derrubar
 *  a sessao nova nem mandar para o login. */
function erroDeSessaoTrocada() {
  const erro = new Error("A sessão mudou durante a operação.");
  erro.sessaoTrocada = true;
  return erro;
}

function writeStoredSession(session) {
  if (typeof window === "undefined") return;

  try {
    if (!session?.accessToken || !session?.refreshToken) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizeSession(session)));
  } catch {
    // Keep the in-memory session if storage is unavailable.
  }
}

function getFirstTextValue(...values) {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (value && typeof value === "object") {
      const nested = getFirstTextValue(value.url, value.path, value.src);
      if (nested) return nested;
    }
  }

  return "";
}

function resolveAccessMediaUrl(value) {
  const url = String(value || "").trim();
  if (!url) return "";

  try {
    const parsed = new URL(url, DEFAULT_ACCESS_API_URL);
    const apiUrl = new URL(DEFAULT_ACCESS_API_URL);
    if (parsed.origin === apiUrl.origin || parsed.hostname === apiUrl.hostname) {
      return `${ACCESS_API_URL}${parsed.pathname}${parsed.search}`;
    }
  } catch {
    // Fall through to the simple handling below.
  }

  if (/^(data:|blob:|https?:\/\/|\/\/)/i.test(url)) {
    const protocol = typeof window !== "undefined" ? window.location.protocol : "https:";
    return url.startsWith("//") ? `${protocol}${url}` : url;
  }

  if (url.startsWith("/access-api/") || url === "/access-api") {
    return url;
  }

  const path = url.startsWith("/") ? url : `/${url}`;
  return `${ACCESS_API_URL}${path}`;
}

function normalizeUsuario(usuario) {
  if (!usuario || typeof usuario !== "object") return usuario || null;

  const photoUrl = resolveAccessMediaUrl(
    getFirstTextValue(
      usuario.foto_url,
      usuario.fotoUrl,
      usuario.foto,
      usuario.avatar_url,
      usuario.avatarUrl,
      usuario.imagem_url,
      usuario.imagem,
      usuario.url,
      usuario.path,
      usuario.src,
      usuario.foto_path
    )
  );

  return {
    ...usuario,
    foto_url: photoUrl,
  };
}

function unwrapPayloadData(payload) {
  return payload?.data ?? payload ?? null;
}

function getUsuarioFromPayload(payload) {
  const data = unwrapPayloadData(payload);

  if (!data || typeof data !== "object") {
    return null;
  }

  return data.usuario || data.user || data.perfil || data;
}

function mergeUsuarioFromPayload(baseUsuario, payload) {
  const data = unwrapPayloadData(payload);
  const candidate = getUsuarioFromPayload(payload);

  if (!candidate || typeof candidate !== "object") {
    return null;
  }

  const normalizedCandidate = normalizeUsuario(candidate);
  const photoUrl =
    normalizedCandidate?.foto_url ||
    resolveAccessMediaUrl(
      getFirstTextValue(
        data?.foto_url,
        data?.fotoUrl,
        data?.foto,
        data?.avatar_url,
        data?.avatarUrl,
        data?.imagem_url,
        data?.imagem,
        data?.url,
        data?.path,
        data?.src,
        data?.foto_path
      )
    );

  return normalizeUsuario({
    ...(baseUsuario || {}),
    ...candidate,
    foto_url: photoUrl || normalizedCandidate?.foto_url || baseUsuario?.foto_url || "",
  });
}

function normalizeSession(session) {
  if (!session || typeof session !== "object") return session;

  return {
    ...session,
    usuario: normalizeUsuario(session.usuario),
  };
}

async function parseResponse(response) {
  if (response.status === 204) return null;

  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    const isHtml = /<!doctype html|<html[\s>]/i.test(text);
    if (isHtml) {
      return {
        erro:
          response.status === 404
            ? "Recurso não encontrado na API. Verifique se o backend publicado possui este endpoint."
            : `A API retornou uma resposta HTML inesperada (${response.status}).`,
      };
    }

    return { erro: text };
  }
}

function buildError(response, payload) {
  // Cadeia de fallback (erro -> detalhe -> detail -> erros -> mensagem ->
  // status) vive em utils/errosApi, junto do `errosPorCampo` que lê o mesmo
  // payload — eram duas cópias da mesma regra. Lá, `erros: {detalhe}` sozinho
  // não vira "detalhe: ..." : não é nome de campo, é a mensagem.
  const error = new Error(mensagemDeErro(payload, response.status));
  error.status = response.status;
  error.payload = payload;
  return error;
}

// Bloco LOG2 (2026-05-25): timeout default 45s para nao deixar requests
// "infinitas" quando o backend trava (Render Free pode hibernar > 50s em
// dias ruins; sem timeout, login fica preso para sempre). 45s cobre o
// cold start tipico (~30s warm-up gunicorn + workers + Postgres).
// Callers que precisam de mais tempo (upload de arquivo grande, geracao
// de PDF) passam { timeoutMs: 120000 }.
const DEFAULT_FETCH_TIMEOUT_MS = 45000;

// Refresh do JWT precisa de timeout maior que o default. Em cold start
// pesado do Render Free, o backend pode demorar 30-60s pra responder o
// primeiro request. Login ja usa 90s pelo mesmo motivo (linha 650).
// Refresh estava em 45s e isso era a causa #1 do "logout fantasma":
// usuario fica inativo 15min+ -> volta -> backend hibernou -> refresh
// timeout em 45s -> clearSession -> /login. Agora aguenta ate ~5min
// (3 tentativas * 90s) antes de desistir.
const REFRESH_FETCH_TIMEOUT_MS = 90000;

// Caminho critico de ENTRADA (/api/me + /api/me/permissoes no boot e logo apos
// o login). Media de 2026-07: o cold start do Render Free chega a ~61s — acima
// dos 45s do default. Ou seja, o primeiro acesso do dia abortava por timeout
// contra um servidor que responderia poucos segundos depois; o usuario via
// erro e o F5 "resolvia" (na 2a vez o backend ja estava quente). Alinhado com
// login (90s) e refresh (90s), que ja tinham essa folga.
const COLD_START_FETCH_TIMEOUT_MS = 90000;

// Tentativas do /api/me no caminho de entrada. Cold start eh "tudo ou nada":
// ou o servidor ja subiu, ou a 1a tentativa o acorda e a 2a o encontra quente.
// 2 tentativas cobrem o caso sem mascarar erro real (que nao eh re-tentado).
const ME_TENTATIVAS_ENTRADA = 2;

// Distingue "servidor ainda acordando" de "servidor respondeu com erro".
// TIMEOUT (abort do AbortController) e NETWORK (fetch rejeitou) nao tem
// `status` porque nunca houve resposta HTTP — sao exatamente os sintomas de
// backend hibernado/subindo, e sao os unicos que vale re-tentar. Qualquer
// coisa com status (401/403/500...) eh resposta real e deve subir na hora.
function ehServidorAcordando(error) {
  return (
    !error?.status && (error?.code === "TIMEOUT" || error?.code === "NETWORK")
  );
}

// Quando AUTH_USE_HTTP_ONLY_COOKIES=True no backend, o refreshToken
// vai em cookie httpOnly e o fetch precisa enviar `credentials: "include"`.
// O HOTFIX HFCORS de 2026-05-22 desativou isso temporariamente. Para
// ligar de novo (sem editar codigo), basta setar REACT_APP_USE_CREDENTIALS=true
// no Vercel + AUTH_USE_HTTP_ONLY_COOKIES=True no Render. Os dois precisam
// ser ligados juntos.
const USE_CREDENTIALS = (process.env.REACT_APP_USE_CREDENTIALS || "false")
  .toLowerCase() === "true";

// Telemetria de sessao encerrada: registra TODA chamada a clearSession
// com motivo identificavel, para diagnosticar logouts "fantasma" em prod.
// DEV: console.warn. PROD: navigator.sendBeacon (nao bloqueia, funciona
// ate enquanto a pagina fecha). Backend pode adicionar endpoint
// /api/telemetria/sessao-encerrada para receber e persistir; enquanto nao
// existir, sendBeacon falha quieto (best-effort).
//
// Motivos canonicos (manter sincronizado com docs):
//   - logout_manual: usuario clicou em sair
//   - refresh_permanent_failure: refresh 401/403 real (blacklistado)
//   - refresh_transient_error: timeout/5xx/network esgotou retries
//     (sessao NAO foi derrubada, so logado para visibilidade)
//   - boot_sem_tokens: useEffect inicial sem tokens no storage
//   - storage_event_logout: outra aba derrubou
// Detecta se o app esta rodando como PWA standalone (Chrome/Edge install,
// Safari Add to Home Screen, etc). Util para telemetria — vai mostrar se
// "logout fantasma" eh especifico de PWA ou tambem acontece em browser.
function detectarPwa() {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
    // iOS Safari nao reporta display-mode; usa navigator.standalone.
    if (window.navigator?.standalone === true) return true;
  } catch {
    /* defensivo */
  }
  return false;
}

export function registrarTelemetriaSessao(motivo, contexto = {}) {
  if (typeof window === "undefined") return;
  const contextoEnriquecido = {
    ...contexto,
    is_pwa: detectarPwa(),
  };
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn(`[AuthContext] sessao:${motivo}`, contextoEnriquecido);
  }
  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const payload = JSON.stringify({
        motivo,
        contexto: contextoEnriquecido,
        pathname: window.location?.pathname || "",
        timestamp: new Date().toISOString(),
        ua: navigator.userAgent || "",
      });
      navigator.sendBeacon(
        `${ACCESS_API_URL}/api/telemetria/sessao-encerrada`,
        new Blob([payload], { type: "application/json" }),
      );
    }
  } catch {
    /* best-effort: nao bloqueia o fluxo de logout */
  }
}

// Bloco LOG2 Camada B (2026-05-25): warm-up proativo do backend Render
// Free. A tela de Login chama warmupAccessApi() no mount enquanto o
// usuario ainda esta digitando email/senha — se o backend estiver
// dormindo, comeca a acordar em paralelo. Quando o usuario clicar
// "Entrar", o ganho de 5-30s sera invisivel. Best-effort: erros sao
// silenciados (proxima request vai relatar o problema real).
//
// Devolve Promise<boolean> (2026-10-24): `true` quando o JWKS respondeu 2xx.
// A tela de entrada usa esse resultado como o status "Operacional" / "Sem
// conexao" do topo. O JWKS e o sinal certo para quem esta prestes a logar:
// publico, sem auth, com CORS, e so responde se as chaves do JWT estao
// configuradas (sem elas nenhum login funciona). O /healthz do backend nao
// serve aqui: e respondido antes do CorsMiddleware, entao o navegador nao
// deixa ler a resposta. Quem so quer acordar o servidor ignora o retorno.
export function warmupAccessApi() {
  try {
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    // Cap em 30s pra nao manter conexao pendurada indefinidamente — se o
    // backend nao acordar em 30s, deixa o proximo request (com timeout 45s)
    // tentar de novo.
    const timerId = controller
      ? setTimeout(() => controller.abort(), 30000)
      : null;
    return fetch(`${ACCESS_API_URL}/api/auth/.well-known/jwks.json`, {
      cache: "no-store",
      signal: controller?.signal,
    })
      .then((resposta) => Boolean(resposta?.ok))
      .catch(() => false /* silencioso — best effort */)
      .finally(() => { if (timerId) clearTimeout(timerId); });
  } catch {
    return Promise.resolve(false);
  }
}

async function fetchAccessApi(endpoint, options) {
  const opts = { ...(options || {}) };
  const timeoutMs = opts.timeoutMs ?? DEFAULT_FETCH_TIMEOUT_MS;
  // Nao repassa timeoutMs ao fetch — nao eh propriedade valida da Fetch API.
  delete opts.timeoutMs;

  let controller;
  let timerId;
  // Permite caller passar o proprio AbortController; caso contrario, criamos
  // um. Pattern idiomatico ja usado em DadosContext.fetchJson() (linhas 38-47).
  if (typeof AbortController !== "undefined" && !opts.signal) {
    controller = new AbortController();
    opts.signal = controller.signal;
    timerId = setTimeout(() => controller.abort(), timeoutMs);
  }

  try {
    // HOTFIX HFCORS (2026-05-22 -> 2026-05-28): credentials="include"
    // agora eh controlado pela env var REACT_APP_USE_CREDENTIALS. Quando
    // o backend tiver AUTH_USE_HTTP_ONLY_COOKIES=True + CORS_ALLOW_
    // CREDENTIALS=True deployado, setar essa env var no Vercel ativa
    // o envio de cookie sem mudar codigo. Default false continua
    // funcionando com o modo legado (refreshToken no body, accessToken
    // no localStorage).
    return await fetch(`${ACCESS_API_URL}${endpoint}`, {
      cache: "no-store",
      ...(USE_CREDENTIALS ? { credentials: "include" } : {}),
      ...opts,
    });
  } catch (error) {
    // AbortError = timeout disparou. Mensagem dedicada para a UI poder
    // mostrar "Tente novamente" em vez do erro generico de rede.
    if (error?.name === "AbortError") {
      const timeoutError = new Error(
        "Servidor demorou demais a responder. Tente novamente em alguns segundos."
      );
      timeoutError.code = "TIMEOUT";
      timeoutError.timeoutMs = timeoutMs;
      timeoutError.originalError = error;
      throw timeoutError;
    }
    // Network error tipico: CORS bloqueado, backend offline, DNS ou rede.
    // Em DEV, log detalhado pra acelerar diagnostico — silenciado em prod.
    if (typeof process !== "undefined" && process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.error(
        "[fetchAccessApi] Network error — possiveis causas:\n"
        + "  1. CORS bloqueado: backend nao autoriza esta origin\n"
        + "     (em DEV, base.py ja tem CORS_ALLOWED_ORIGIN_REGEXES "
        + "cobrindo localhost — se nao funcionou, conferir deploy do Render).\n"
        + "  2. Backend offline / cold start (Render free demora ~30s na 1a request apos idle).\n"
        + `  3. URL errada — atual: ${ACCESS_API_URL}\n`
        + "Erro original:", error,
      );
    }
    const networkError = new Error(
      "Não foi possivel conectar a API de acessos. Verifique a publicacao do app e tente novamente."
    );
    networkError.code = "NETWORK";
    networkError.originalError = error;
    throw networkError;
  } finally {
    if (timerId) clearTimeout(timerId);
  }
}

async function publicRequest(endpoint, options = {}) {
  const headers = new Headers(options.headers || {});
  const hasFormData = options.body instanceof FormData;

  if (!hasFormData && options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetchAccessApi(endpoint, {
    ...options,
    headers,
  });
  const payload = await parseResponse(response);

  if (!response.ok) {
    throw buildError(response, payload);
  }

  return payload;
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(() => readStoredSession());
  const [usuario, setUsuario] = useState(() => readStoredSession()?.usuario || null);
  const [permissoes, setPermissoes] = useState(() => readStoredSession()?.permissoes || null);
  const [primeiroAcesso, setPrimeiroAcesso] = useState(() => Boolean(readStoredSession()?.primeiroAcesso));
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  // ToastProvider eh ancestral de AuthProvider em App.jsx — useToast funciona.
  // Apos refactor do ToastContext (Bloco Parenteses 2), useToast() retorna
  // referencias 100% estaveis — entao na pratica `toast` nao muda mais.
  // Mas mantemos `toastRef` como defesa em profundidade: se um dia o
  // ToastContext for alterado, o request() continua estavel sem `toast`
  // na dep do useCallback. Padrao usado em libs React maduras (React Query).
  const toast = useToast();
  const toastRef = useRef(toast);
  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  // --- Step-up MFA recente ---
  // Quando uma request retorna 403 com mensagem "MFA recente", a propria
  // funcao `request` empilha um resolver aqui e dispara o modal global.
  // O modal coleta o codigo TOTP e chama `verifyMfaStepUp`; em sucesso,
  // todos os resolvers pendentes recebem `true` (refazendo as requests
  // originais), em cancelamento recebem `false` (rejeitando com o erro
  // original 403). Aglutina N requests concorrentes num unico modal.
  const mfaStepUpQueueRef = useRef([]);
  const [mfaStepUpOpen, setMfaStepUpOpen] = useState(false);

  // Mutex de refresh: garante que N requests paralelos que recebem 401
  // disparem APENAS UMA chamada a POST /api/auth/refresh. Sem isso, com
  // ROTATE_REFRESH_TOKENS=True + BLACKLIST_AFTER_ROTATION=True no backend,
  // a corrida invalida o token antigo enquanto outras chamadas ainda
  // estavam tentando usar — resultado: usuario deslogado "do nada" mesmo
  // tendo sessao valida (o vencedor da corrida ja gravou token novo no
  // localStorage). Pattern Promise singleton: quem chegar primeiro cria
  // a Promise; os demais retornam a MESMA Promise e compartilham o
  // resultado. Limpo no finally (sucesso ou falha).
  const refreshPromiseRef = useRef(null);

  // Geracao da sessao: sobe toda vez que a PESSOA deste navegador muda
  // (login, logout, outra aba entrou como outro usuario). Pedido que comecou
  // numa geracao e termina em outra e resposta atrasada de quem ja saiu:
  // o resultado e descartado, nunca gravado por cima da sessao nova.
  //
  // Auditoria do chat (2026-09-29): `refreshSession` e `loadMe` liam a sessao,
  // esperavam a rede (o refresh, ate 3 x 90 s) e gravavam o que tinham lido —
  // se A saia e B entrava nesse meio, B ficava com tokens ou nome e permissoes
  // de A. Renovar o token da MESMA pessoa (nesta ou em outra aba) nao muda a
  // geracao.
  const geracaoRef = useRef(0);
  // A geracao criada pela queda da PROPRIA sessao (refresh recusado): essa
  // nao e troca de pessoa — quem pediu segue para o /login, como sempre.
  const geracaoDaQuedaRef = useRef(-1);
  const novaGeracao = useCallback(() => {
    geracaoRef.current += 1;
    refreshPromiseRef.current = null;
  }, []);

  // Guard de redirect: se varios requests paralelos falham no refresh
  // (ex.: backend down), todos chegariam ao window.location.assign("/login")
  // e empilhariam N navegacoes redundantes — flutter visual + lixo no
  // history.back(). Marca true na primeira chamada; reset acontece
  // naturalmente quando o browser navega (a pagina recarrega).
  const redirecionandoLoginRef = useRef(false);

  // Quantos <MfaStepUpModal /> estao montados agora.
  //
  // A fila acima so e drenada por esse modal. Se ninguem o montou, quem
  // espera na fila espera para sempre: a request nao resolve nem
  // rejeita, a tela fica em esqueleto de carregamento e o toast desse
  // 403 e suprimido de proposito (o modal e que deveria explicar).
  // Aconteceu de verdade no host do painel, onde nao existe o shell do
  // ERP que montava o modal. Contar consumidores torna a suposicao
  // verificavel em tempo de execucao em vez de combinada por convencao.
  const mfaStepUpConsumidoresRef = useRef(0);
  const registrarConsumidorDeStepUp = useCallback(() => {
    mfaStepUpConsumidoresRef.current += 1;
    return () => {
      mfaStepUpConsumidoresRef.current = Math.max(
        0, mfaStepUpConsumidoresRef.current - 1,
      );
    };
  }, []);

  const completeMfaStepUp = useCallback((success) => {
    const queue = mfaStepUpQueueRef.current;
    mfaStepUpQueueRef.current = [];
    setMfaStepUpOpen(false);
    queue.forEach(({ resolve }) => resolve(Boolean(success)));
  }, []);

  const cancelMfaStepUp = useCallback(() => completeMfaStepUp(false), [completeMfaStepUp]);

  const persistSession = useCallback((nextSession) => {
    const normalizedSession = normalizeSession(nextSession);

    setSession(normalizedSession);
    setUsuario(normalizedSession?.usuario || null);
    setPermissoes(normalizedSession?.permissoes || null);
    setPrimeiroAcesso(Boolean(normalizedSession?.primeiroAcesso));
    writeStoredSession(normalizedSession);
  }, []);

  const clearSession = useCallback(() => {
    // Limpa qualquer Promise de refresh em voo. Sanity: se a sessao
    // foi derrubada, a Promise em curso ja nao tem sentido — o proximo
    // request precisa partir do zero (provavelmente vai para /login).
    novaGeracao();
    // MT-4.7 (multi-tenant): remove as chaves operacionais prefixadas pelo
    // tenant corrente (filtros, rascunhos, agendamentos migrados para
    // utils/tenantStorage). PRECISA rodar antes de persistSession(null) —
    // o código do tenant é lido da própria sessão persistida.
    limparChavesDoTenant();
    persistSession(null);
    // Revoga os object URLs de fotos em cache: libera memoria e evita que a
    // foto de um usuario apareca para o proximo num terminal compartilhado.
    clearAuthenticatedImageCache();
    // Mesmo motivo do cache de fotos: os snapshots de paineis vivem num Map de
    // modulo sem cleanup. Sem zerar, um agendamento do proximo usuario achava
    // o snapshot "fresco" (<10min) do anterior e exportava os numeros dele —
    // bypass de RBAC entre usuarios do mesmo tenant, no mesmo navegador.
    limparSnapshots();
    // A notificação do chat é de quem saiu: o endereço deste navegador no
    // serviço de push morre agora, e nenhum aviso dele aparece para o próximo.
    soltarDoAparelho();
    // Security Sprint 1 (A15, audit 2026-05-21): logout em terminal
    // compartilhado (ex.: kiosk de PCP, computador da fabrica) deixava
    // filtros operacionais e estado de UI no localStorage sob o prefixo
    // `pharuerp:*`. Proximo usuario via os filtros do anterior.
    //
    // Limpamos tudo que comeca com `pharuerp:` exceto preferencias
    // cosmeticas neutras (sidebar collapsed + theme) — perder essas
    // a cada logout seria desagradavel sem ganho de seguranca.
    try {
      const PRESERVAR = new Set([
        // Bugfix 2026-05-28: ThemeContext persiste em "pharuerp:is-dark"
        // (boolean stringificado), nao em "pharuerp:theme". Chave antiga
        // estava sendo limpa silenciosamente — usuario perdia darkmode
        // a cada logout. Mantemos "pharuerp:theme" no allowlist tambem
        // por seguranca caso alguma versao antiga ainda use.
        "pharuerp:is-dark",
        "pharuerp:theme",
        // PREF-UI (2026-08-04): o allowlist acima cobria SO o claro/escuro.
        // Cor de destaque, modo, densidade e animacoes eram varridas em todo
        // logout e o app voltava ao ACCENT_DEFAULT ("violet") — a causa do
        // "sempre volta pro tema roxo", que acontecia ate no MESMO
        // computador, nao so ao trocar de dispositivo.
        //
        // Continuam sendo espelho local: a fonte da verdade agora e o perfil
        // no servidor (SincronizadorPreferencias). Preserva-las evita a
        // piscada do padrao ate o /api/me responder no proximo login.
        "pharuerp:theme-mode",
        "pharuerp:accent-color",
        "pharuerp:density",
        "pharuerp:animacoes",
        // Estado de navegacao. Os nomes reais tem DOIS pontos ("sidebar:")
        // — o allowlist antigo listava "pharuerp:sidebar-collapsed", com
        // hifen, que NENHUM codigo escreve. Protegia um nome morto enquanto
        // a chave de verdade era apagada.
        "pharuerp:sidebar:collapsed",
        "pharuerp:sidebar:top-level-aberto",
      ]);
      const chavesParaRemover = [];
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const chave = window.localStorage.key(i);
        if (
          chave &&
          ["pharuerp:", "dash-pcp:", "dasherp:", "dash-erp:"].some((p) =>
            chave.startsWith(p),
          ) &&
          !PRESERVAR.has(chave)
        ) {
          chavesParaRemover.push(chave);
        }
      }
      chavesParaRemover.forEach((chave) =>
        window.localStorage.removeItem(chave),
      );
    } catch (_) {
      // localStorage indisponivel (modo privado, etc) - log silencioso
    }
  }, [novaGeracao, persistSession]);

  const refreshSession = useCallback(async () => {
    // Mutex: se ja ha uma Promise em voo, todos os callers compartilham.
    // Resolve a race do simplejwt + BLACKLIST_AFTER_ROTATION (ver comentario
    // em refreshPromiseRef).
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const geracao = geracaoRef.current;
    const sessaoTrocou = () => {
      if (geracaoRef.current === geracao) return false;
      registrarTelemetriaSessao("refresh_descartado_sessao_trocou");
      return true;
    };

    const promise = (async () => {
      const current = readStoredSession();
      // Security Sprint 2 (C1, audit 2026-05-21): em prod com httpOnly
      // cookie, refreshToken nao esta em localStorage — vem do cookie via
      // credentials: "include" no fetch. Se nao temos accessToken local,
      // ainda assim podemos tentar refresh (browser envia cookie).
      if (!current?.accessToken && !current?.refreshToken) {
        registrarTelemetriaSessao("refresh_sem_tokens");
        clearSession();
        return null;
      }

      const bodyPayload = current?.refreshToken
        ? { refresh: current.refreshToken }
        : {};

      // Retry exponencial em falhas transitorias (timeout/5xx/network).
      // Backoff: 1s, 3s. Total cobertura: 3 tentativas x 90s = ~5 min de
      // tolerancia para Render Free acordar. Cold start tipico (~30s)
      // responde na 1a tentativa sem precisar do retry.
      // Em testes, backoff zero para nao depender de fake timers.
      const BACKOFFS_MS = process.env.NODE_ENV === "test"
        ? [0, 0]
        : [1000, 3000];
      const MAX_TENTATIVAS = 3;
      let ultimoErro;

      for (let tentativa = 0; tentativa < MAX_TENTATIVAS; tentativa += 1) {
        try {
          const payload = await publicRequest("/api/auth/refresh", {
            method: "POST",
            body: JSON.stringify(bodyPayload),
            timeoutMs: REFRESH_FETCH_TIMEOUT_MS,
          });
          // A pessoa mudou enquanto o refresh viajava: estes tokens sao de
          // quem ja saiu. Gravar aqui devolveria a sessao dela ao navegador.
          if (sessaoTrocou()) throw erroDeSessaoTrocada();
          const next = {
            ...current,
            accessToken: payload?.data?.access,
            // Em modo cookie, payload.data.refresh nao vem — mantem o
            // ultimo refresh conhecido (pode ser undefined em prod, mas
            // backend usa cookie em sucessivos refresh).
            refreshToken: payload?.data?.refresh || current?.refreshToken,
          };

          persistSession(next);
          return next.accessToken;
        } catch (error) {
          if (error?.sessaoTrocada) throw error;
          ultimoErro = error;
          const transitorio = (
            error?.code === "TIMEOUT"
            || error?.code === "NETWORK"
            || (typeof error?.status === "number"
                && error.status >= 500
                && error.status < 600)
          );
          if (transitorio && tentativa < MAX_TENTATIVAS - 1) {
            // Aguarda o backoff e tenta de novo. Nao chama clearSession —
            // sessao ainda pode ser valida, eh o backend que esta indispo.
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => setTimeout(r, BACKOFFS_MS[tentativa]));
            continue;
          }
          break;
        }
      }

      // O refresh de quem ja saiu falhou: nao e motivo para derrubar (nem para
      // acusar instabilidade a) a sessao de quem entrou.
      if (sessaoTrocou()) throw erroDeSessaoTrocada();

      // Classifica o erro final:
      //   - transitorio (timeout/5xx/network) esgotou retries:
      //     NAO derruba sessao. Lanca erro com transient=true para o
      //     caller decidir (toast "conexao instavel", nao /login).
      //   - permanente (401/403): refresh blacklistado/invalido,
      //     sessao morta de verdade. Aih sim clearSession.
      const transitorio = (
        ultimoErro?.code === "TIMEOUT"
        || ultimoErro?.code === "NETWORK"
        || (typeof ultimoErro?.status === "number"
            && ultimoErro.status >= 500
            && ultimoErro.status < 600)
      );
      if (transitorio) {
        registrarTelemetriaSessao("refresh_transient_error", {
          code: ultimoErro?.code || null,
          status: ultimoErro?.status || null,
          tentativas: MAX_TENTATIVAS,
        });
        const err = new Error(
          "Conexão instavel durante renovacao de sessao. Tente novamente.",
        );
        err.transient = true;
        err.originalError = ultimoErro;
        throw err;
      }

      // Falha permanente: sessao realmente expirou (refresh blacklistado,
      // usuario desativado, chave JWT rotacionada, etc).
      registrarTelemetriaSessao("refresh_permanent_failure", {
        status: ultimoErro?.status || null,
        mensagem: ultimoErro?.message || null,
      });
      clearSession();
      geracaoDaQuedaRef.current = geracaoRef.current;
      return null;
    })();

    refreshPromiseRef.current = promise;
    try {
      return await promise;
    } finally {
      // So solta o mutex se ele ainda e o NOSSO: a troca de geracao ja o
      // zerou, e um refresh novo (da pessoa nova) pode estar em voo.
      if (refreshPromiseRef.current === promise) refreshPromiseRef.current = null;
    }
  }, [clearSession, persistSession]);

  const request = useCallback(
    async (endpoint, options = {}, retry = true) => {
      // silentError: quando true, NAO dispara toast.error automatico em
      // falha. Use quando a tela ja trata o erro inline (ex.: form com
      // mensagem de erro por campo) ou quando o catch faz toast proprio.
      const { silentError = false, ...fetchOptions } = options;
      const current = readStoredSession();
      const geracao = geracaoRef.current;
      const headers = new Headers(fetchOptions.headers || {});
      const hasFormData = fetchOptions.body instanceof FormData;

      if (current?.accessToken) {
        headers.set("Authorization", `Bearer ${current.accessToken}`);
      }

      if (!hasFormData && fetchOptions.body && !headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
      }

      const response = await fetchAccessApi(endpoint, {
        ...fetchOptions,
        headers,
      });
      const payload = await parseResponse(response);

      if (response.status === 401 && retry) {
        let nextAccessToken = null;
        try {
          nextAccessToken = await refreshSession();
        } catch (refreshErr) {
          if (refreshErr?.transient || refreshErr?.sessaoTrocada) {
            // Refresh teve erro transitorio (timeout/5xx/network) e
            // esgotou retries. Sessao NAO foi derrubada pelo refreshSession.
            // Lanca o 401 original como erro normal para a UI tratar
            // (toast/error boundary) — SEM redirecionar para /login.
            // Na proxima acao do usuario o refresh sera tentado de novo.
            throw buildError(response, payload);
          }
          // Erro nao-transitorio nao deveria escapar do refreshSession
          // (ele ja captura tudo), mas defensivamente rejogamos.
          throw refreshErr;
        }
        // Este pedido era de quem estava logado quando ele saiu. Se a pessoa
        // mudou, refaze-lo seria executa-lo com o token de OUTRA pessoa — e
        // mandar para /login derrubaria quem acabou de entrar.
        if (geracaoRef.current !== geracao
            && geracaoRef.current !== geracaoDaQuedaRef.current) {
          throw buildError(response, payload);
        }
        if (nextAccessToken) {
          return request(endpoint, options, false);
        }
        // Defesa em profundidade contra a race do refresh: o nosso
        // refresh falhou (token blacklistado, por exemplo), mas outra
        // aba ou outro caller pode ter gravado token novo no localStorage
        // enquanto rodavamos. Antes de derrubar a sessao, re-checa o
        // storage — se ha token diferente do que enviamos, refaz a
        // request com ele em vez de mandar /login.
        const apos = readStoredSession();
        if (apos?.accessToken && apos.accessToken !== current?.accessToken) {
          return request(endpoint, options, false);
        }
        // SS3.B.6 (M13, audit 2026-05-21): refresh falhou — sessao
        // expirou definitivamente. Antes desta correcao o usuario ficava
        // em tela autenticada com sessao morta (clearSession ja limpou
        // session/usuario mas o componente da tela atual nao reage; o
        // proximo click em qualquer botao tambem da 401, loop visual
        // de erros).
        //
        // Solucao: forcar navegacao para /login. Usa window.location
        // (assign + replace) em vez de useNavigate porque request eh
        // um useCallback fora do scope de Router e roteamento custom
        // pode estar em qualquer lugar da arvore.
        try {
          if (typeof window !== "undefined" && window.location
              && !redirecionandoLoginRef.current) {
            const here = window.location.pathname;
            // Evita loop se ja estamos em /login (login fluxo proprio
            // tambem usa request via publicRequest? Nao: publicRequest
            // nao chega aqui. Defensivo mesmo assim.)
            if (!here.startsWith("/login")
                && !here.startsWith("/primeiro-acesso")
                && !here.startsWith("/mfa-verify")
                && !here.startsWith("/p/")
                && !here.startsWith("/producao/terminal")) {
              redirecionandoLoginRef.current = true;
              window.location.assign("/login");
            }
          }
        } catch (_) {
          // navegacao falhou — segue para o throw do err 401
        }
      }

      if (!response.ok) {
        const err = buildError(response, payload);

        // Interceptor: 403 "MFA recente" abre modal global de step-up.
        // Diferente de 401: o usuario continua autenticado, so precisa
        // revalidar TOTP pra renovar `ultimo_login_mfa_em` no backend.
        // A request original eh refeita transparente apos o sucesso.
        const ehMfaRecente = (
          response.status === 403
          && /MFA recente/i.test(err.message || "")
        );
        // So entra na fila se houver quem a atenda. Sem consumidor, cair
        // no fluxo normal de erro: um 403 visivel e muito melhor que uma
        // request pendurada em silencio, que a pessoa le como travamento.
        const temQuemAtenda = mfaStepUpConsumidoresRef.current > 0;
        if (ehMfaRecente && temQuemAtenda) {
          const stepUpOk = await new Promise((resolve) => {
            mfaStepUpQueueRef.current.push({ resolve });
            setMfaStepUpOpen(true);
          });
          if (stepUpOk) {
            // Refaz a request — `retry=false` para evitar loop de refresh,
            // ja que esta nao eh 401.
            return request(endpoint, options, false);
          }
          // Cancelou: cai no fluxo normal de erro abaixo.
        }

        // Auto-toast global de erro: garante que NENHUMA falha de API
        // fica invisivel para o usuario (Bloco Parenteses). Suprime
        // automaticamente para 401 (refresh ja trata), 403 MFA recente
        // (modal global lida com a UX) e quando o caller pediu
        // silentError explicitamente.
        if (
          !silentError
          && response.status !== 401
          && !(ehMfaRecente && temQuemAtenda)
        ) {
          try {
            // 429 (rate-limit): dedupKey GLOBAL — 1 toast so para
            // qualquer rate-limit independente do endpoint que disparou.
            // Evita 50 toasts simultaneos quando varios widgets batem
            // o limite ao mesmo tempo (Bloco Parenteses 2 — fix throttle storm).
            const dedupKey = response.status === 429
              ? "rate-limit"
              : `req-err-${response.status}-${endpoint}`;
            // Acessa via REF — request() nao precisa de `toast` na dep
            // do useCallback (defesa em profundidade).
            toastRef.current.error(
              err.message || `Erro ${response.status}`,
              { dedupKey },
            );
          } catch (_) {
            // toast nao disponivel (teste, etc) — falha silenciosa.
          }
        }
        throw err;
      }

      return payload;
    },
    // CRITICO: `toast` REMOVIDO das deps. Antes causava loop infinito
    // (request recriado a cada toast adicionado -> componentes refazem fetch
    // -> mais toasts -> mais fetches -> 429 storm). Agora request eh estavel.
    [refreshSession]
  );

  // Chama POST /api/auth/mfa/step-up. Retorna o payload do backend ou
  // throw em falha. Reusa o proprio `request` para herdar headers de
  // auth + tratamento de 401 + toast (silentError porque o modal
  // mostra erro inline).
  const verifyMfaStepUp = useCallback(
    async (codigo) => {
      const payload = await request("/api/auth/mfa/step-up", {
        method: "POST",
        body: JSON.stringify({ codigo }),
        silentError: true,
      });
      return payload?.data || payload;
    },
    [request],
  );

  const loadMe = useCallback(async ({ preservePhoto = false, timeoutMs } = {}) => {
    // `timeoutMs` opcional: o caminho de ENTRADA (boot e pos-login) passa
    // COLD_START_FETCH_TIMEOUT_MS para aguentar o backend acordando. Sem ele
    // vale o default de 45s — os chamadores de rotina (visibilitychange,
    // uploadPhoto, trocar unidade) seguem exatamente como antes.
    const opcoesRequest = timeoutMs ? { timeoutMs } : undefined;
    const geracao = geracaoRef.current;
    const [mePayload, permissoesPayload] = await Promise.all([
      request("/api/me", opcoesRequest),
      request("/api/me/permissoes", opcoesRequest),
    ]);
    const current = readStoredSession();
    // A resposta e de quem estava logado quando o pedido saiu. Se a pessoa
    // mudou, gravar aqui poria o nome e as permissoes de uma com os tokens
    // da outra.
    if (geracaoRef.current !== geracao) {
      registrarTelemetriaSessao("me_descartado_sessao_trocou");
      return current;
    }
    const meData = unwrapPayloadData(mePayload);
    const apiUser = normalizeUsuario(getUsuarioFromPayload(mePayload));
    // preservePhoto (usado so pelo uploadPhoto): se o /api/me ainda nao refletiu
    // a foto recem-enviada e volta sem foto_url, mantem a que ja esta em sessao.
    // Mantido restritivo (so quando apiUser nao tem foto) para nao alterar o
    // comportamento do refresh por visibilitychange, que tambem usa preservePhoto.
    const nextUsuario =
      preservePhoto && apiUser && !apiUser.foto_url && current?.usuario?.foto_url
        ? { ...apiUser, foto_url: current.usuario.foto_url }
        : apiUser;
    const next = {
      ...(current || {}),
      usuario: nextUsuario,
      permissoes: permissoesPayload?.data || null,
      primeiroAcesso: Boolean(meData?.primeiro_acesso ?? nextUsuario?.primeiro_acesso),
      // P1 (2026-06-18): o /api/me passou a recalcular o enrollment MFA, entao
      // o banner some sem relogar quando a exigencia muda. Se o backend ainda
      // nao mandar a chave (janela de deploy), preserva o valor atual.
      mfaEnrollmentRequired:
        meData?.mfa_enrollment_required !== undefined
          ? Boolean(meData.mfa_enrollment_required)
          : Boolean(current?.mfaEnrollmentRequired),
    };

    persistSession(next);
    return next;
  }, [persistSession, request]);

  // Carrega a sessao no caminho de ENTRADA (boot e logo apos o login): folga de
  // cold start + nova tentativa quando o servidor esta apenas acordando.
  // Existe para boot e login compartilharem a MESMA politica — a divergencia
  // entre eles (login aguentava 90s, mas o /api/me logo em seguida abortava aos
  // 45s) era o que transformava um login bem-sucedido em erro vermelho.
  const carregarSessaoDeEntrada = useCallback(
    async (opcoes = {}) => {
      let ultimoErro = null;
      for (let tentativa = 1; tentativa <= ME_TENTATIVAS_ENTRADA; tentativa += 1) {
        try {
          return await loadMe({
            ...opcoes,
            timeoutMs: COLD_START_FETCH_TIMEOUT_MS,
          });
        } catch (error) {
          ultimoErro = error;
          // Erro COM status eh resposta real do servidor (401/403/500) —
          // repetir nao muda nada e ainda atrasa o feedback ao usuario.
          if (!ehServidorAcordando(error)) throw error;
        }
      }
      throw ultimoErro;
    },
    [loadMe],
  );

  const login = useCallback(
    async ({ email, password }) => {
      setAuthError(null);
      // Bloco LOG2.F (2026-05-25): timeout 90s especifico para login.
      // Render Free pode ter cold start ainda mais lento que 45s default
      // quando Web Service + Postgres free hibernaram ao mesmo tempo
      // (cada um leva 20-40s pra acordar; em serie pode > 60s). bcrypt
      // tambem leva ~200-500ms por design. 90s da folga sem ser absurdo.
      // No painel da Pharu o backend recusa quem não é da equipe com a
      // mesma resposta de senha errada, antes do segundo fator.
      const superficie = ehHostDoPainel() ? "painel" : "erp";
      const payload = await publicRequest("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password, superficie }),
        timeoutMs: 90000,
      });
      const data = payload?.data || {};

      // Sprint 10 — cenario 2: MFA exigido + credencial ativa. Backend
      // retorna apenas `mfa_token` (5min, scope='mfa'). NAO persistir
      // sessao aqui — o token nao serve como access_token. Quem chama
      // navega para /mfa-verify com este token no state e completa o
      // 2-step la.
      if (data.mfa_required) {
        return {
          status: "mfa_required",
          mfaToken: data.mfa_token,
          primeiroAcesso: Boolean(data.primeiro_acesso),
        };
      }

      // Cenarios 1 (sem MFA) e 3 (MFA exigido mas sem credencial — backend
      // ja devolveu access_token real + flag `mfa_enrollment_required`).
      const baseSession = {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        usuario: normalizeUsuario(data.usuario),
        permissoes: null,
        primeiroAcesso: Boolean(data.primeiro_acesso),
        // Sprint 10 — cenario 3: usuario entra mas precisa configurar MFA
        // imediatamente. Banner persistente le esta flag e abre o painel.
        mfaEnrollmentRequired: Boolean(data.mfa_enrollment_required),
      };

      novaGeracao();
      persistSession(baseSession);

      if (!data.primeiro_acesso) {
        // A autenticacao JA foi concluida e a sessao JA esta persistida acima.
        // Se o /api/me falhar aqui porque o backend ainda esta subindo, NAO
        // transformamos um login BEM-SUCEDIDO em erro vermelho na tela de
        // login. Era exatamente este o bug do "primeiro acesso no dispositivo":
        // o usuario logava, via a caixa vermelha, dava F5 — e "funcionava",
        // porque a sessao ja tinha sido gravada no localStorage.
        // Entramos com o que temos; `useEffect` de auto-recuperacao (abaixo)
        // busca as permissoes sozinho assim que o servidor responder.
        try {
          await carregarSessaoDeEntrada();
        } catch (error) {
          // Erro real (403, 500...) continua sendo erro de login.
          if (!ehServidorAcordando(error)) throw error;
          registrarTelemetriaSessao("me_falhou_pos_login", {
            code: error?.code || null,
          });
        }
      }

      return { status: "ok", session: baseSession };
    },
    [carregarSessaoDeEntrada, novaGeracao, persistSession]
  );

  // Sprint 10 — conclui o 2-step de login. Recebe o mfa_token do passo 1
  // (mantido em React state da MfaVerify, NUNCA em localStorage) + codigo
  // TOTP ou recovery code. Backend (MfaVerifyView) aceita ambos com
  // fallback automatico. Em sucesso, persiste a sessao real e carrega /me.
  const verifyMfaLogin = useCallback(
    async ({ mfaToken, codigo }) => {
      const payload = await publicRequest("/api/auth/mfa/verify", {
        method: "POST",
        body: JSON.stringify({ mfa_token: mfaToken, codigo }),
      });
      const data = payload?.data || {};
      const baseSession = {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        usuario: normalizeUsuario(data.usuario),
        permissoes: null,
        primeiroAcesso: Boolean(data.primeiro_acesso),
        recuperacaoUsada: Boolean(data.recuperacao_usada),
      };
      novaGeracao();
      persistSession(baseSession);
      if (!data.primeiro_acesso) {
        await loadMe();
      }
      return baseSession;
    },
    [loadMe, novaGeracao, persistSession]
  );

  // Sprint 10 — apos enrollment obrigatorio concluido (cenario 3), o
  // banner chama esta funcao para limpar a flag e parar de aparecer.
  const dismissMfaEnrollmentRequired = useCallback(() => {
    const current = readStoredSession();
    if (!current) return;
    persistSession({ ...current, mfaEnrollmentRequired: false });
  }, [persistSession]);

  const logout = useCallback(async () => {
    const current = readStoredSession();

    // Telemetria ANTES da chamada remota: garantir que o motivo eh
    // capturado mesmo se a request /api/auth/logout falhar e nao
    // retornarmos ao codigo logo abaixo.
    registrarTelemetriaSessao("logout_manual");

    if (current?.refreshToken && current?.accessToken) {
      // O corpo leva o refresh token que esta no storage NA HORA do envio.
      // Com o access vencido, o 401 dispara um refresh com ROTACAO: a nova
      // tentativa automatica do `request` reenviava o token ANTIGO (o corpo
      // ja estava montado) e o token novo, valido por dias, ficava fora da
      // blacklist. Por isso a nova tentativa e feita aqui, relendo o storage.
      const revogar = () => request("/api/auth/logout", {
        method: "POST",
        body: JSON.stringify({ refresh_token: readStoredSession()?.refreshToken }),
        silentError: true,
      }, false);
      try {
        await revogar();
      } catch (error) {
        if (error?.status === 401) {
          try {
            if (await refreshSession()) await revogar();
          } catch {
            // Sem rede ou refresh ja invalido: segue para a limpeza local.
          }
        }
        // Session is cleared locally even if the remote token is already invalid.
      }
    }

    clearSession();
  }, [clearSession, refreshSession, request]);

  const changePassword = useCallback(
    async ({ senha_atual, nova_senha, confirmar_nova_senha }, { silentError = false } = {}) => {
      const payload = await request("/api/auth/change-password", {
        method: "POST",
        body: JSON.stringify({ senha_atual, nova_senha, confirmar_nova_senha }),
        silentError,
      });
      await loadMe();
      return payload;
    },
    [loadMe, request]
  );

  const updateProfile = useCallback(
    async (data) => {
      const payload = await request("/api/me", {
        method: "PATCH",
        body: JSON.stringify(data),
      });
      await loadMe();
      return payload;
    },
    [loadMe, request]
  );

  const uploadPhoto = useCallback(
    async (file) => {
      const formData = new FormData();
      formData.append("foto", file);

      const payload = await request("/api/me/foto", {
        method: "POST",
        body: formData,
      });
      const current = readStoredSession();
      const usuarioComFoto = mergeUsuarioFromPayload(current?.usuario, payload);

      if (usuarioComFoto) {
        persistSession({
          ...(current || {}),
          usuario: usuarioComFoto,
        });
      }

      await loadMe({ preservePhoto: true });

      // Cache-bust APOS o loadMe (que traz a foto_url canonica do /api/me): como
      // essa URL nao muda entre uploads, sem o ?v= os componentes montados
      // (avatar do Header/Perfil) continuariam exibindo o blob antigo do cache
      // compartilhado. Mudar o src forca o hook a buscar a imagem nova. Feito
      // por ultimo para o loadMe nao sobrescrever a URL bustada.
      const aposUpload = readStoredSession();
      const baseFoto = aposUpload?.usuario?.foto_url || "";
      if (baseFoto) {
        const fotoBustada = `${baseFoto}${baseFoto.includes("?") ? "&" : "?"}v=${Date.now()}`;
        persistSession({
          ...aposUpload,
          usuario: { ...aposUpload.usuario, foto_url: fotoBustada },
        });
      }

      return payload;
    },
    [loadMe, persistSession, request]
  );

  const removePhoto = useCallback(async () => {
    const payload = await request("/api/me/foto", { method: "DELETE" });
    const current = readStoredSession();

    if (current?.usuario) {
      persistSession({
        ...current,
        usuario: normalizeUsuario({ ...current.usuario, foto_url: "" }),
      });
    }

    await loadMe();
    return payload;
  }, [loadMe, persistSession, request]);

  useEffect(() => {
    let active = true;

    // 2026-05-28 — Warm-up imediato no boot do AuthProvider (nao soh na
    // tela de login). Por que: usuario que abre a app instalada (PWA)
    // pula direto a tela de login se ja tem session no localStorage;
    // o primeiro request real (loadMe) cai num backend Render Free
    // hibernado e pode dar timeout antes do retry pegar. Pingando JWKS
    // em paralelo, o backend ja esta acordando durante o boot do React.
    // Best-effort: erro silencioso (proxima request reporta problema real).
    warmupAccessApi();

    (async () => {
      const current = readStoredSession();
      if (!current?.accessToken || !current?.refreshToken) {
        if (active) setLoading(false);
        return;
      }

      try {
        await carregarSessaoDeEntrada();
        if (active) setAuthError(null);
      } catch (error) {
        if (error.status === 401) {
          let refreshed = null;
          try {
            refreshed = await refreshSession();
          } catch (refreshErr) {
            // Transient: sessao preservada, so registra mensagem. UI
            // permite o usuario reload manual; proxima request vai
            // tentar refresh de novo.
            if (refreshErr?.transient) {
              if (active) setAuthError(refreshErr.message);
            } else {
              throw refreshErr;
            }
          }
          if (refreshed) {
            try {
              await carregarSessaoDeEntrada();
            } catch (innerError) {
              // BUG fix (2026-05-28): loadMe falhar APOS refresh OK
              // NAO eh motivo para logout. O token novo ja foi gravado
              // pelo refresh — sessao esta valida. Antes desta correcao,
              // qualquer 502/timeout em /api/me derrubava o usuario.
              // Agora: apenas seta authError e a UI mostra mensagem +
              // permite reload.
              registrarTelemetriaSessao("loadme_falhou_pos_refresh", {
                status: innerError?.status || null,
                code: innerError?.code || null,
              });
              if (active) setAuthError(innerError.message);
            }
          }
        } else {
          if (active) setAuthError(error.message);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [clearSession, carregarSessaoDeEntrada, refreshSession]);

  // Sincronizacao entre abas: outra aba pode ter feito login, logout ou
  // renovado o token. Sem este listener, cada aba opera com snapshot
  // proprio em memoria — mesma causa secundaria da race do refresh, e
  // ainda piora UX (logout em uma aba nao reflete na outra).
  //
  // `storage` event so dispara em outras abas (nao na que escreveu),
  // entao nao ha risco de loop.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handler = (event) => {
      if (event.key !== STORAGE_KEY) return;

      // Outra aba fez logout (removeu a chave).
      if (event.newValue === null) {
        registrarTelemetriaSessao("storage_event_logout");
        setSession(null);
        setUsuario(null);
        setPermissoes(null);
        setPrimeiroAcesso(false);
        novaGeracao();
        // Higiene: revoga os object URLs de fotos em cache quando outra aba
        // desloga (consistente com clearSession nesta aba).
        clearAuthenticatedImageCache();
        limparSnapshots();
        return;
      }

      try {
        const parsed = normalizeSession(JSON.parse(event.newValue));
        if (!parsed?.accessToken) return;
        // Outra aba entrou como OUTRA pessoa: o que esta em voo nesta aba e
        // de quem saiu. (Token renovado da mesma pessoa nao muda a geracao.)
        const antes = event.oldValue ? normalizeSession(JSON.parse(event.oldValue)) : null;
        if (String(antes?.usuario?.id ?? "") !== String(parsed.usuario?.id ?? "")) {
          novaGeracao();
          clearAuthenticatedImageCache();
          limparSnapshots();
        }
        // Atualiza estado in-memory apenas se o token mudou — evita
        // re-render desnecessario quando outras abas escrevem o mesmo
        // valor (ex.: tocar foto, mexer em flag).
        setSession((atual) => {
          if (atual?.accessToken === parsed.accessToken
              && atual?.refreshToken === parsed.refreshToken) {
            return atual;
          }
          return parsed;
        });
        setUsuario(parsed.usuario || null);
        setPermissoes(parsed.permissoes || null);
        setPrimeiroAcesso(Boolean(parsed.primeiroAcesso));
      } catch {
        // payload corrompido — ignora (proximo write valido sincroniza)
      }
    };

    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [novaGeracao]);

  // Refetch de /api/me/permissoes ao retomar foco da janela.
  //
  // Bugfix RBAC 2026-05-28: quando admin altera permissoes do cargo do
  // usuario, o backend agora bumpa Usuario.permissoes_versao (cache key
  // muda automaticamente). Mas se o usuario nao recarregar a pagina, o
  // frontend continua com o snapshot de permissoes do login. Solucao:
  // ao voltar pra aba/janela (visibilitychange), reload silencioso das
  // permissoes — em segundo plano, sem afetar a UI. Custo: 1 request
  // pequena (~5kb) por troca de aba.
  // Auto-recuperacao das permissoes (rede de seguranca do caminho de entrada).
  //
  // Invariante: usuario autenticado SEMPRE precisa de `permissoes` carregadas —
  // sem elas a sidebar fica vazia e telas com gate mostram "voce nao tem
  // permissao para acessar esta pagina". A sessao nasce com `permissoes: null`
  // (baseSession do login) e so eh preenchida pelo /api/me/permissoes; se
  // aquela chamada falhou porque o backend ainda estava acordando, o usuario
  // ficava preso nesse estado ate dar F5 manualmente.
  // Este efeito fecha o buraco: detecta o estado invalido e recarrega sozinho,
  // com espera crescente (2s, 4s, 8s, 16s). Silencioso — nao mostra erro; ou a
  // tela se completa sozinha, ou o usuario continua com o que ja tinha.
  const [tentativaPermissoes, setTentativaPermissoes] = useState(0);

  // Zera o contador quando a sessao muda ou as permissoes chegam, para que um
  // novo login (ou nova queda) tenha o ciclo de tentativas completo de novo.
  useEffect(() => {
    if (permissoes || !session?.accessToken) setTentativaPermissoes(0);
  }, [permissoes, session?.accessToken]);

  useEffect(() => {
    if (!session?.accessToken) return undefined;
    if (permissoes) return undefined;
    // Primeiro acesso troca a senha antes de qualquer coisa — nao ha o que
    // recarregar enquanto o usuario esta nesse fluxo.
    if (primeiroAcesso) return undefined;
    if (tentativaPermissoes >= 4) return undefined;

    const espera = 2000 * 2 ** tentativaPermissoes;
    const timer = setTimeout(() => {
      carregarSessaoDeEntrada().catch(() => {
        // Falhou de novo: incrementa para o efeito reagendar com espera maior.
        setTentativaPermissoes((n) => n + 1);
      });
    }, espera);
    return () => clearTimeout(timer);
  }, [
    session?.accessToken,
    permissoes,
    primeiroAcesso,
    tentativaPermissoes,
    carregarSessaoDeEntrada,
  ]);

  const ultimoReloadVisibilidadeRef = useRef(0);
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (!session?.accessToken) return undefined;

    const handler = () => {
      if (document.visibilityState !== "visible") return;
      // Fase 6 (perf): throttle de 30s — evita refetch de /me + /me/permissoes
      // a cada troca RAPIDA de aba (alt-tab em rajada). O reload de permissoes
      // ao voltar continua valendo; so nao repete em sequencia.
      const agora = Date.now();
      if (agora - ultimoReloadVisibilidadeRef.current < 30000) return;
      ultimoReloadVisibilidadeRef.current = agora;
      // silencioso — falha aqui nao desloga nem incomoda o usuario.
      // Reusa loadMe que ja faz refetch de /me + /me/permissoes em paralelo.
      loadMe({ preservePhoto: true }).catch(() => {});
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [session?.accessToken, loadMe]);

  // Warm-up periodico do Render Free durante sessao ativa.
  //
  // Por que: backend Render Free hiberna apos ~50s sem requests. Quando
  // o usuario volta apos longo periodo de inatividade (15min+), o access
  // token ja expirou e o proximo refresh cai num backend frio — pode
  // chegar a timeout, falha de refresh, sessao morta.
  //
  // Como: a cada 4 minutos (bem dentro do TTL de 15min do access token),
  // se houve atividade do usuario (mousemove/keydown) nos ultimos 60s,
  // pingamos o endpoint publico JWKS (super-leve, sem auth) so para
  // manter o backend acordado. Sem atividade, nao pingamos — nao
  // desperdiçamos a quota do Render free se a pessoa saiu da maquina.
  //
  // Impacto: ~15 req/h por usuario ativo. Na cliente-piloto (~10 usuarios pico),
  // ~150 req/h extras — desprezivel.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!session?.accessToken) return undefined;

    let ultimoEvento = Date.now();
    const marcarAtivo = () => { ultimoEvento = Date.now(); };
    window.addEventListener("mousemove", marcarAtivo, { passive: true });
    window.addEventListener("keydown", marcarAtivo, { passive: true });
    window.addEventListener("touchstart", marcarAtivo, { passive: true });

    const INTERVALO_MS = 4 * 60 * 1000; // 4min
    const JANELA_ATIVIDADE_MS = 60 * 1000; // 60s

    const id = window.setInterval(() => {
      if (Date.now() - ultimoEvento > JANELA_ATIVIDADE_MS) return;
      // Best-effort; nao bloqueia, nao captura erros — proxima request
      // real vai reportar problema concreto se houver.
      try {
        fetch(`${ACCESS_API_URL}/api/auth/.well-known/jwks.json`, {
          cache: "no-store",
        }).catch(() => {});
      } catch {
        /* silencioso */
      }
    }, INTERVALO_MS);

    return () => {
      window.clearInterval(id);
      window.removeEventListener("mousemove", marcarAtivo);
      window.removeEventListener("keydown", marcarAtivo);
      window.removeEventListener("touchstart", marcarAtivo);
    };
  }, [session?.accessToken]);

  // Sprint B da migracao RBAC: `is_master` vem explicitamente do backend
  // (`/api/me/permissoes`), derivado da flag `Cargo.is_master_role` (nao
  // mais comparacao por nome). Fallback `false` em payloads antigos.
  const isMaster = useMemo(
    () => Boolean(permissoes?.is_master),
    [permissoes]
  );

  // Sprint A removeu o signal de sync `pode_ver_precos`. O payload do
  // backend agora deriva esse boolean do override `preco:view`. Mantemos
  // `usuario?.pode_ver_precos` no OR enquanto o campo legado ainda existe
  // (some no Sprint D); fonte canonica eh `permissoes?.pode_ver_precos`.
  const canViewPrices = Boolean(
    permissoes?.pode_ver_precos || usuario?.pode_ver_precos
  );

  // Sprint B: `efetivasSet` agora le APENAS o array `efetivas[]` do payload.
  // Eliminamos o fallback que derivava `<pagina>:view` de `paginas[]` —
  // depois do Sprint A o backend sempre entrega `efetivas`, e o fallback
  // mascarava o bug "override grava mas frontend nao reflete".
  const efetivasSet = useMemo(() => {
    const efetivas = Array.isArray(permissoes?.efetivas) ? permissoes.efetivas : [];
    return new Set(efetivas);
  }, [permissoes]);

  const hasPermission = useCallback(
    (...args) => {
      if (isMaster) return true;
      // Aceita tanto `hasPermission("recurso:acao")` quanto
      // `hasPermission("recurso", "acao")`.
      const codigo = args.length === 1 ? args[0] : `${args[0]}:${args[1]}`;
      if (!codigo) return true;
      return efetivasSet.has(codigo);
    },
    [efetivasSet, isMaster]
  );


  const hasAnyPermission = useCallback(
    (codigos) => {
      if (isMaster) return true;
      if (!Array.isArray(codigos) || codigos.length === 0) return true;
      return codigos.some((c) => efetivasSet.has(c));
    },
    [efetivasSet, isMaster]
  );

  const hasAllPermissions = useCallback(
    (codigos) => {
      if (isMaster) return true;
      if (!Array.isArray(codigos) || codigos.length === 0) return true;
      return codigos.every((c) => efetivasSet.has(c));
    },
    [efetivasSet, isMaster]
  );

  const canRead = useCallback((recurso) => hasPermission(recurso, "view"), [hasPermission]);
  const canCreate = useCallback((recurso) => hasPermission(recurso, "create"), [hasPermission]);
  const canEdit = useCallback((recurso) => hasPermission(recurso, "edit"), [hasPermission]);
  const canDelete = useCallback((recurso) => hasPermission(recurso, "delete"), [hasPermission]);
  const canApprove = useCallback((recurso) => hasPermission(recurso, "approve"), [hasPermission]);
  const canWrite = useCallback(
    (recurso) => hasPermission(recurso, "edit") || hasPermission(recurso, "create"),
    [hasPermission]
  );

  // Produtos Pharu que esta unidade contratou (ADR-0057). O backend
  // devolve a lista pela MESMA funcao que monta o `aud` do token, entao
  // a tela nunca oferece um produto que o outro lado recusaria com 401.
  //
  // `temProduto` NAO passa pelo bypass de Master: ser Master e questao de
  // permissao, e ter comprado o produto e questao de contrato. Um Master
  // de cliente que so assinou o ERP nao deve ver porta para o otimizador.
  const produtosContratados = useMemo(
    () => new Set(permissoes?.produtos_contratados || []),
    [permissoes]
  );
  const temProduto = useCallback(
    (slug) => produtosContratados.has(slug),
    [produtosContratados]
  );

  // Módulos do ERP contratados pela unidade (ADR-0102, `config/modulos.js`).
  // Mesmo raciocínio do `temProduto`: sem bypass de Master, porque ter
  // contratado é questão de contrato. A lista vem da mesma função que faz as
  // rotas do módulo responderem 404, então o menu nunca oferece o que o
  // backend recusa.
  const temModulo = useMemo(() => verificadorDeModulos(permissoes), [permissoes]);

  const value = useMemo(
    () => ({
      apiBaseUrl: ACCESS_API_URL,
      usuario,
      permissoes,
      primeiroAcesso,
      // Sprint 10 — flag de enrollment obrigatorio pendente (cenario 3).
      mfaEnrollmentRequired: Boolean(session?.mfaEnrollmentRequired),
      loading,
      authError,
      authenticated: Boolean(session?.accessToken && usuario),
      isMaster,
      canViewPrices,
      temProduto,
      temModulo,
      // API RBAC (recurso:acao) — fonte unica apos Sprint C da migracao.
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      canRead,
      canCreate,
      canEdit,
      canDelete,
      canApprove,
      canWrite,
      request,
      login,
      // Sprint 10 — fluxo MFA 2-step.
      verifyMfaLogin,
      dismissMfaEnrollmentRequired,
      // Step-up MFA: estado do modal + handlers consumidos pelo
      // <MfaStepUpModal />, montado no shell do ERP e no host do painel.
      // O modal registra-se aqui ao montar; sem registro, o interceptor
      // nao segura request nenhuma esperando por ele.
      registrarConsumidorDeStepUp,
      mfaStepUpOpen,
      verifyMfaStepUp,
      completeMfaStepUp,
      cancelMfaStepUp,
      logout,
      changePassword,
      updateProfile,
      uploadPhoto,
      removePhoto,
      reloadSession: loadMe,
    }),
    [
      authError,
      canApprove,
      canCreate,
      canDelete,
      canEdit,
      canRead,
      canViewPrices,
      canWrite,
      changePassword,
      dismissMfaEnrollmentRequired,
      hasAllPermissions,
      hasAnyPermission,
      hasPermission,
      mfaStepUpOpen,
      registrarConsumidorDeStepUp,
      verifyMfaStepUp,
      completeMfaStepUp,
      cancelMfaStepUp,
      isMaster,
      loadMe,
      loading,
      login,
      logout,
      permissoes,
      primeiroAcesso,
      removePhoto,
      request,
      session,
      temModulo,
      temProduto,
      updateProfile,
      uploadPhoto,
      usuario,
      verifyMfaLogin,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
